import { useEffect, useRef, useState } from 'react'
import { LEVEL_VISUALIZER_DELAY } from '@/features/twilio/TwilioConstants'

export function useSpeakerLevels(
  context: AudioContext,
  element: HTMLAudioElement,
  deviceId: string | null,
) {
  const [levels, setLevels] = useState(0)

  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null)
  if (!sourceNodeRef.current) {
    sourceNodeRef.current = context.createMediaElementSource(element)
  }

  const setOutputVolRef = useRef<((vol: number) => void) | null>(null)
  useEffect(() => {
    setOutputVolRef.current = (vol: number) => setLevels(vol)
  }, [setLevels])

  useEffect(() => {
    const promise = (async () => {
      if (context.state === 'suspended') await context.resume()

      const analyser = context.createAnalyser()
      analyser.smoothingTimeConstant = 0.4
      analyser.fftSize = 64

      sourceNodeRef.current!!.connect(analyser)

      let nodes: AudioNode[] = [analyser]
      let audio: HTMLAudioElement | null = null

      if (
        'setSinkId' in HTMLAudioElement.prototype &&
        typeof HTMLAudioElement.prototype.setSinkId === 'function'
      ) {
        const dstNode = context.createMediaStreamDestination()
        analyser.connect(dstNode)
        const dstAudio = new Audio()
        dstAudio.loop = false
        dstAudio.srcObject = dstNode.stream

        // @ts-ignore
        await dstAudio.setSinkId!!(deviceId)
        await dstAudio.play()
        nodes.push(dstNode)

        audio = dstAudio
      } else {
        analyser.connect(context.destination)
      }

      const fDataBytes = new Uint8Array(analyser.frequencyBinCount)

      const volumeEvent = () => {
        analyser.getByteFrequencyData(fDataBytes)
        const volume =
          fDataBytes.reduce((sum, val) => sum + val, 0) / fDataBytes.length
        const clipped = Math.min(255, volume)
        setOutputVolRef.current?.(clipped / 255.0)
      }

      const interval = window.setInterval(volumeEvent, LEVEL_VISUALIZER_DELAY)

      return {
        nodes,
        audio,
        interval,
      }
    })()

    return () => {
      promise.then((cleaner) => {
        if (cleaner) {
          for (const node of cleaner.nodes) node.disconnect()

          if (cleaner.audio) cleaner.audio.remove()

          window.clearInterval(cleaner.interval)
        }
      })
    }
  }, [deviceId])

  return {
    levels,
  }
}
